/* eslint-disable react-hooks/exhaustive-deps */
import {
    useState, useEffect, FC, useRef,
} from 'react';
import { FormattedMessage } from 'react-intl';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import Grow from '@ui/Grow';
import Badge from '@material-ui/core/Badge';
import Avatar from '@material-ui/core/Avatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@ui/Typography';
import Skeleton from '@material-ui/lab/Skeleton';

import { marineColor } from '@ui/styles/colors';
import RenderDelayer from '@ui/RenderDelayer';
import chatThumbnail from '../../../assets/images/chat-thumbnail.jpg';
import {
    chatQuestionWrapperStyle,
    chatQuestionTextWrapperStyle,
    chatQuestionGrowStyle,
    chatQuestionListItemAvatarStyle,
    chatQuestionAvatarStyle,
    chatQuestionNameStyle,
    chatQuestionListItemTextStyle,
} from './styles';
import IChatQuestionProperties from './types';

const ChatQuestion: FC<IChatQuestionProperties> = ({
    children,
    animationDelay,
    shouldAnimateOnMount,
    onMountCallback,
    onMountCallbackDelay,
    isExternal,
}) => {
    const [isQuestionDisplayed, setIsQuestionDisplayed] = useState(!shouldAnimateOnMount);
    const [hasTriggeredOnMountCallback, setHasTriggeredOnMountCallback] = useState<boolean>(false);
    const reference = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const displayChatQuestion = () => { setIsQuestionDisplayed(true); };
        const displayTimeout = shouldAnimateOnMount && animationDelay !== undefined && setTimeout(displayChatQuestion, 1000 + animationDelay);

        return () => (displayTimeout ? clearTimeout(displayTimeout) : undefined);
    }, [
        animationDelay,
        shouldAnimateOnMount,
        setIsQuestionDisplayed,
    ]);

    useEffect(() => {
        let onMountCallbackTimeout: ReturnType<typeof setTimeout>;

        if (isQuestionDisplayed && !hasTriggeredOnMountCallback && reference && reference.current && onMountCallback) {
            setHasTriggeredOnMountCallback(true);
            setTimeout(() => { onMountCallback(reference); }, animationDelay !== undefined && onMountCallbackDelay !== undefined ? onMountCallbackDelay - animationDelay : 1);
        }

        return () => (onMountCallbackTimeout ? clearTimeout(onMountCallbackTimeout) : undefined);
    }, [reference, onMountCallback, onMountCallbackDelay, isQuestionDisplayed]);

    return (
        <RenderDelayer delay={shouldAnimateOnMount && animationDelay ? animationDelay : 0}>
            <ListItem component="div" style={chatQuestionWrapperStyle} ref={reference}>
                <ListItemAvatar style={chatQuestionListItemAvatarStyle}>
                    <Grow in timeout={shouldAnimateOnMount ? 800 : 0}>
                        <Badge
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            color="secondary"
                            overlap="circular"
                            badgeContent=" "
                            variant="dot"
                        >
                            <Avatar
                                style={chatQuestionAvatarStyle}
                                src={isExternal && window.artisanPublicData?.companyName ? (window.artisanPublicData?.profileImageUrl ? window.artisanPublicData?.profileImageUrl : undefined) : chatThumbnail}
                                alt="chat-thumbnail"
                                color="default"
                            />
                        </Badge>
                    </Grow>
                </ListItemAvatar>

                <div style={chatQuestionGrowStyle}>
                    <ListItemText
                        disableTypography
                        style={chatQuestionListItemTextStyle}
                        primary={(
                            <Grow in timeout={shouldAnimateOnMount ? 1000 : 0}>
                                <Typography
                                    variant="caption"
                                    variantMapping={{ caption: 'span' }}
                                    style={chatQuestionNameStyle}
                                >
                                    {!isExternal
                                        ? <FormattedMessage id="lilmAvatar" tagName="span" />
                                        : (window.artisanPublicData?.firstName
                                            ? (
                                                <>
                                                    {`${window.artisanPublicData?.firstName} `}
                                                    <small style={{ opacity: 0.3, color: marineColor }}>{`- ${window.artisanPublicData?.companyName}`}</small>
                                                </>
                                            )
                                            : `${window.artisanPublicData?.companyName}`)}
                                </Typography>
                            </Grow>
                        )}
                        secondary={(
                            <Grow in timeout={shouldAnimateOnMount ? 1000 : 0}>
                                <Typography
                                    variant="subtitle2"
                                    variantMapping={{ subtitle2: 'h1' }}
                                    style={chatQuestionTextWrapperStyle}
                                >
                                    {isQuestionDisplayed
                                        ? children
                                        : (
                                            <>
                                                <Skeleton style={{ width: 120 }} />
                                                <Skeleton style={{ width: 80 }} />
                                            </>
                                        )}
                                </Typography>
                            </Grow>
                        )}
                    />
                </div>
            </ListItem>
        </RenderDelayer>
    );
};

ChatQuestion.defaultProps = {
    animationDelay       : 0,
    onMountCallbackDelay : 0,
    shouldAnimateOnMount : false,
};

export default ChatQuestion;
